import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
} from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    width: '12px',
    height: '12px',
    marginRight: '4px',
    flexShrink: 0,
    alignSelf: 'center',
  },
  green: {
    backgroundColor: '#22c55e', // Verde mais moderno
  },
  yellow: {
    backgroundColor: '#eab308', // Amarelo mais moderno
  },
  red: {
    backgroundColor: '#ef4444', // Vermelho mais moderno
  },
  unknown: {
    backgroundColor: '#6b7280', // Cinza mais moderno
  },
  textBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 6px',
    borderRadius: '8px',
    fontSize: '10px',
    fontWeight: 'normal',
    marginRight: '4px',
  },
  greenText: {
    backgroundColor: '#f0fdf4',
    color: '#166534',
    border: '1px solid #22c55e',
  },
  yellowText: {
    backgroundColor: '#fefce8',
    color: '#a16207',
    border: '1px solid #eab308',
  },
  redText: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #ef4444',
  },
  unknownText: {
    backgroundColor: '#f9fafb',
    color: '#374151',
    border: '1px solid #6b7280',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  statusText: {
    fontSize: '12px',
    fontWeight: '500',
  },
  timeInfo: {
    fontSize: '10px',
    color: '#6b7280',
  },
}));

const IdleStatusBadge = ({
  idleStatus = null, // Agora recebe o objeto completo ou null
  variant = 'circle', // 'circle', 'text', ou 'detailed'
  showTooltip = true,
  size = 'small', // 'small', 'medium', 'large'
  showTimeInfo = false, // Mostrar informações de tempo
}) => {
  const classes = useStyles();

  // Se idleStatus for uma string (legado), converter para objeto
  const statusData =
    typeof idleStatus === 'string'
      ? { status: idleStatus, percentage: 0, minutesIdle: 0, timeLimit: 0 }
      : idleStatus;

  const status = statusData?.status || 'unknown';

  const getStatusColor = status => {
    switch (status) {
      case 'green':
        return '#22c55e'; // Verde
      case 'yellow':
        return '#eab308'; // Amarelo
      case 'red':
        return '#ef4444'; // Vermelho
      default:
        return '#6b7280'; // Cinza
    }
  };

  const getStatusInfo = status => {
    switch (status) {
      case 'green':
        return {
          color: classes.green,
          textColor: classes.greenText,
          icon: (
            <CheckCircleIcon
              style={{
                fontSize:
                  size === 'small'
                    ? '10px'
                    : size === 'medium'
                      ? '14px'
                      : '18px',
              }}
            />
          ),
          text: 'Normal',
          tooltip: `Ticket normal - ${statusData?.minutesIdle || 0}min ocioso de ${statusData?.timeLimit || 0}min (${statusData?.percentage || 0}%)`,
        };
      case 'yellow':
        return {
          color: classes.yellow,
          textColor: classes.yellowText,
          icon: (
            <WarningIcon
              style={{
                fontSize:
                  size === 'small'
                    ? '10px'
                    : size === 'medium'
                      ? '14px'
                      : '18px',
              }}
            />
          ),
          text: 'Atenção',
          tooltip: `Ticket em atenção - ${statusData?.minutesIdle || 0}min ocioso de ${statusData?.timeLimit || 0}min (${statusData?.percentage || 0}%)`,
        };
      case 'red':
        return {
          color: classes.red,
          textColor: classes.redText,
          icon: (
            <ErrorIcon
              style={{
                fontSize:
                  size === 'small'
                    ? '10px'
                    : size === 'medium'
                      ? '14px'
                      : '18px',
              }}
            />
          ),
          text: 'Crítico',
          tooltip: `Ticket crítico - ${statusData?.minutesIdle || 0}min ocioso de ${statusData?.timeLimit || 0}min (${statusData?.percentage || 0}%)`,
        };
      default:
        return {
          color: classes.unknown,
          textColor: classes.unknownText,
          icon: null,
          text: 'Desconhecido',
          tooltip: 'Status de ociosidade desconhecido',
        };
    }
  };

  const statusInfo = getStatusInfo(status);

  const renderBadge = () => {
    if (variant === 'circle') {
      return (
        <AccessTimeIcon
          style={{
            fontSize:
              size === 'small' ? '16px' : size === 'medium' ? '20px' : '24px',
            marginRight: '4px',
            flexShrink: 0,
            alignSelf: 'center',
            color: getStatusColor(status),
          }}
        />
      );
    }

    if (variant === 'detailed') {
      return (
        <AccessTimeIcon
          style={{
            fontSize:
              size === 'small' ? '16px' : size === 'medium' ? '20px' : '24px',
            marginRight: '4px',
            flexShrink: 0,
            alignSelf: 'center',
            color: getStatusColor(status),
          }}
        />
      );
    }

    return (
      <AccessTimeIcon
        style={{
          fontSize:
            size === 'small' ? '16px' : size === 'medium' ? '20px' : '24px',
          marginRight: '4px',
          flexShrink: 0,
          alignSelf: 'center',
          color: getStatusColor(status),
        }}
      />
    );
  };

  const badge = renderBadge();

  if (showTooltip) {
    return (
      <Tooltip title={statusInfo.tooltip} arrow placement='top'>
        {badge}
      </Tooltip>
    );
  }

  return badge;
};

export default IdleStatusBadge;
